/**
 * Chargement et suivi du code d'un exercice HTML/CSS.
 *
 * Ce hook resout le workspace de depart en une seule passe, selon un ordre de
 * priorite explicite, puis en conserve un instantane. C'est cet instantane qui
 * alimente le bouton "Retablir" : auparavant il etait pris avant l'arrivee de
 * la reponse du serveur et contenait donc toujours l'exemple par defaut, si
 * bien que "Retablir" ecrasait le travail de l'eleve au lieu de le ramener a
 * son point de depart.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useUserApi } from '../../../api/user/userApp';
import { HtmlCssConfigContent, HtmlCssWorkspace } from '../core/types';
import { deserializeWorkspace } from '../core/workspace';
import { DEFAULT_CSS_CODE, DEFAULT_HTML_CODE } from '../core/config';
import { readLocalSnapshot } from './useHtmlCssPersistence';

interface UseHtmlCssWorkspaceOptions {
  lessonId: number;
  config: HtmlCssConfigContent;
  /** En mode administration, aucune reponse d'eleve n'est chargee. */
  isAdmin: boolean;
  /** Code impose par la vue parente (administration). */
  controlledWorkspace?: HtmlCssWorkspace;
}

export interface HtmlCssWorkspaceState {
  workspace: HtmlCssWorkspace;
  /** Etat initial, cible du bouton "Retablir". */
  initialWorkspace: HtmlCssWorkspace;
  loading: boolean;
  setHtml: (html: string) => void;
  setCss: (css: string) => void;
  restoreInitial: () => void;
  /** Identifiant de la reponse existante, ou 0 s'il n'y en a pas. */
  answerId: number;
  /** Vrai si le code affiche provient d'une sauvegarde locale non synchronisee. */
  restoredFromLocal: boolean;
}

/** Workspace de reference defini par l'enseignant, ou exemple par defaut. */
const resolveReferenceWorkspace = (config: HtmlCssConfigContent): HtmlCssWorkspace =>
  deserializeWorkspace(config.adminWorkspace) ?? {
    html: DEFAULT_HTML_CODE,
    css: DEFAULT_CSS_CODE,
  };

export const useHtmlCssWorkspace = ({
  lessonId,
  config,
  isAdmin,
  controlledWorkspace,
}: UseHtmlCssWorkspaceOptions): HtmlCssWorkspaceState => {
  const { getUserAnswerById } = useUserApi();

  // `useUserApi` ne memoise pas ses fonctions : leur identite change a chaque
  // rendu. Les lire via une ref empeche l'effet de chargement de se
  // redeclencher en boucle, sans dependre d'un correctif en amont.
  const getUserAnswerByIdRef = useRef(getUserAnswerById);
  useEffect(() => {
    getUserAnswerByIdRef.current = getUserAnswerById;
  });

  const [workspace, setWorkspace] = useState<HtmlCssWorkspace>(() =>
    resolveReferenceWorkspace(config)
  );
  const [initialWorkspace, setInitialWorkspace] = useState<HtmlCssWorkspace>(workspace);
  const [loading, setLoading] = useState(!isAdmin);
  const [answerId, setAnswerId] = useState(0);
  const [restoredFromLocal, setRestoredFromLocal] = useState(false);

  // Evite d'appliquer une reponse arrivee apres un changement de lecon.
  const requestedLessonRef = useRef(lessonId);

  useEffect(() => {
    requestedLessonRef.current = lessonId;
    const reference = resolveReferenceWorkspace(config);

    /** Fixe le code affiche et le point de retour du bouton "Retablir". */
    const applyWorkspace = (resolved: HtmlCssWorkspace, fromLocal = false) => {
      setWorkspace(resolved);
      setInitialWorkspace(resolved);
      setRestoredFromLocal(fromLocal);
      setLoading(false);
    };

    // Mode administration : le code vient de la vue parente, jamais du serveur.
    if (isAdmin) {
      applyWorkspace(controlledWorkspace ?? reference);
      return;
    }

    if (!lessonId) {
      applyWorkspace(reference);
      return;
    }

    let cancelled = false;
    setLoading(true);

    getUserAnswerByIdRef.current(lessonId)
      .then(response => {
        if (cancelled || requestedLessonRef.current !== lessonId) return;

        const answer = response?.data;
        if (answer?.id) setAnswerId(answer.id);

        const saved = deserializeWorkspace(answer?.workspace);
        const local = readLocalSnapshot(lessonId);

        // Une sauvegarde locale n'existe que si le serveur avait refuse une
        // reponse : elle est donc plus recente que ce qu'il renvoie.
        if (local) {
          applyWorkspace({ html: local.html, css: local.css }, true);
          return;
        }

        applyWorkspace(saved ?? reference);
      })
      .catch(() => {
        if (cancelled || requestedLessonRef.current !== lessonId) return;

        // Serveur injoignable : on repart du travail local s'il existe, sinon
        // du workspace de reference. L'echec n'est pas signale a l'eleve ici,
        // il le sera au moment de la sauvegarde.
        const local = readLocalSnapshot(lessonId);
        if (local) {
          applyWorkspace({ html: local.html, css: local.css }, true);
          return;
        }
        applyWorkspace(reference);
      });

    return () => {
      cancelled = true;
    };
    // `config` est volontairement reduit a son workspace de reference : les
    // listes d'aide n'ont aucune incidence sur le code charge. L'appel API est
    // lu par ref, il n'a donc pas a figurer ici.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId, isAdmin, config.adminWorkspace]);

  // En administration, la vue parente reste la source de verite du code.
  useEffect(() => {
    if (!isAdmin || !controlledWorkspace) return;
    setWorkspace(current =>
      current.html === controlledWorkspace.html && current.css === controlledWorkspace.css
        ? current
        : controlledWorkspace
    );
  }, [isAdmin, controlledWorkspace]);

  const setHtml = useCallback((html: string) => {
    setWorkspace(current => (current.html === html ? current : { ...current, html }));
  }, []);

  const setCss = useCallback((css: string) => {
    setWorkspace(current => (current.css === css ? current : { ...current, css }));
  }, []);

  const restoreInitial = useCallback(() => {
    setWorkspace(initialWorkspace);
  }, [initialWorkspace]);

  return {
    workspace,
    initialWorkspace,
    loading,
    setHtml,
    setCss,
    restoreInitial,
    answerId,
    restoredFromLocal,
  };
};
